import { AlertTriangle, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { api } from '@/lib/api';
import { useAuth } from '@/features/auth/AuthContext';

interface Props {
  roomId: string;
  roomName: string;
  /** Who created this room, if known — its own creator may end it too, not just admins/editors. */
  createdByEmail?: string | null;
  /** Called after a successful close so the parent can drop out of the room. */
  onClosed?: () => void;
}

export function EndSessionDialog({ roomId, roomName, createdByEmail, onClosed }: Props) {
  const { user } = useAuth();
  const isCreator = !!user && !!createdByEmail && user.email.toLowerCase() === createdByEmail.toLowerCase();
  const canManage = isCreator || user?.role === 'admin' || user?.role === 'editor';

  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) setConfirm('');
  }, [open]);

  async function endSession() {
    setSaving(true);
    try {
      const row = await api.closeRoom(roomId);
      toast.success('Room closed', {
        description: `Disconnected ${row.kicked} client${row.kicked === 1 ? '' : 's'}.`,
      });
      setOpen(false);
      onClosed?.();
    } catch (err) {
      toast.error('Close failed', {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="destructive"
          size="sm"
          disabled={!canManage}
          title={canManage ? 'End this room permanently' : 'Only the room’s creator, editors, or admins can end a session'}
        >
          <X className="h-4 w-4" /> End session
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            End session &ldquo;{roomName}&rdquo;?
          </DialogTitle>
          <DialogDescription>
            Disconnects every connected AR client and retires this URL
            permanently. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="end-session-confirm" className="text-sm">
            Type <span className="font-mono font-semibold text-foreground">{roomName}</span> to confirm.
          </Label>
          <Input
            id="end-session-confirm"
            autoFocus
            autoComplete="off"
            placeholder={roomName}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={saving}
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" size="sm" disabled={saving}>Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            size="sm"
            disabled={saving || confirm !== roomName}
            onClick={endSession}
          >
            {saving
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><X className="h-4 w-4" /> End session</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
